// src/stores/ContentInteractionStore.ts - ENHANCED VERSION (Bước 1)

import { makeAutoObservable, runInAction } from 'mobx';
import contentInteractionService from '@/services/contentInteractionService';
import type { RootStore } from './index';

export interface ContentInteraction {
    id: string;
    type: 'view' | 'like' | 'download' | 'share' | 'rating';
    contentId: string;
    userId: string;
    data?: any;
    createdAt: string;
}

export interface ContentStats {
    viewCount: number;
    likeCount: number;
    downloadCount: number;
    shareCount: number;
    averageRating: number;
    ratingCount: number;
}

class ContentInteractionStore {
    rootStore: RootStore;

    // ✅ NEW: Enhanced state management
    interactions: ContentInteraction[] = [];
    contentStats: Record<string, ContentStats> = {};
    userInteractions: Record<string, ContentInteraction[]> = {};

    // ✅ NEW: Per-content loading states (thay vì global isLoading)
    loadingStates: Record<string, boolean> = {};

    // ✅ NEW: Error handling per content
    errors: Record<string, string | null> = {};

    // ✅ KEEP: Backward compatibility
    isLoading = false;
    error: string | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, { rootStore: false });
    }

    // ====================================
    // ✅ NEW: Enhanced Getters
    // ====================================

    getStats(contentId: string): ContentStats {
        return this.contentStats[contentId] || {
            viewCount: 0,
            likeCount: 0,
            downloadCount: 0,
            shareCount: 0,
            averageRating: 0,
            ratingCount: 0
        };
    }

    isLikedByUser(contentId: string): boolean {
        const userInteractions = this.userInteractions[contentId] || [];
        const likeInteraction = userInteractions.find(i => i.type === 'like');
        return likeInteraction?.data?.isLiked || false;
    }

    getUserRating(contentId: string): number | null {
        const userInteractions = this.userInteractions[contentId] || [];
        const ratingInteraction = userInteractions.find(i => i.type === 'rating');
        return ratingInteraction?.data?.rating || null;
    }

    // ✅ NEW: Enhanced loading state management
    isContentLoading(contentId: string): boolean {
        return this.loadingStates[contentId] || false;
    }

    getContentError(contentId: string): string | null {
        return this.errors[contentId] || null;
    }

    // ====================================
    // ✅ NEW: Batch Data Loading
    // ====================================

    async loadContentData(contentId: string) {
        if (!contentId) {
            console.warn('ContentInteractionStore: No contentId provided');
            return;
        }

        this.setContentLoading(contentId, true);
        this.clearContentError(contentId);

        try {
            // console.log(`🔄 Loading interaction data for content: ${contentId}`);

            // Load data in parallel but handle errors independently
            const [statsResult, interactionsResult] = await Promise.allSettled([
                this.getContentStats(contentId),
                this.rootStore.authStore.currentUser
                    ? this.getUserInteractions(contentId)
                    : Promise.resolve([])
            ]);

            if (statsResult.status === 'rejected') {
                console.warn('Failed to load content stats:', statsResult.reason);
            }

            if (interactionsResult.status === 'rejected') {
                console.warn('Failed to load user interactions:', interactionsResult.reason);
            }

            // console.log(`✅ Content data loaded for: ${contentId}`);
            return { stats: this.getStats(contentId), interactions: this.userInteractions[contentId] || [] };

        } catch (error: any) {
            console.error(`❌ Error loading content data for ${contentId}:`, error);
            this.setContentError(contentId, error.message || 'Không thể tải dữ liệu');
            throw error;
        } finally {
            this.setContentLoading(contentId, false);
        }
    }

    // ====================================
    // ✅ ENHANCED: Existing Actions
    // ====================================

    async recordView(contentId: string, additionalData?: any) {
        try {
            console.log(`👁️ Recording view for content: ${contentId}`);

            const result = await contentInteractionService.recordView(contentId, {
                ...additionalData,
                timestamp: new Date().toISOString()
            });

            runInAction(() => {
                this.updateContentStats(contentId, 'view', 1);
            });

            return result;
        } catch (error: any) {
            console.warn('Failed to record view:', error);
            // Don't throw for views - it's non-critical
            return null;
        }
    }

    async toggleLike(contentId: string, likeType: 'like' | 'love' | 'bookmark' | 'favorite' = 'like') {
        if (!this.rootStore.authStore.currentUser) {
            throw new Error('Vui lòng đăng nhập để thích tác phẩm');
        }

        this.setContentLoading(contentId, true);

        try {
            console.log(`❤️ Toggling like for content: ${contentId}`);

            const currentlyLiked = this.isLikedByUser(contentId);
            const result = await contentInteractionService.toggleLike(contentId, likeType);

            runInAction(() => {
                // Optimistic update
                const delta = result.isLiked ? 1 : -1;
                this.updateContentStats(contentId, 'like', delta);
                this.updateUserInteraction(contentId, 'like', result);
            });

            console.log(`✅ Like toggled: ${result.isLiked ? 'liked' : 'unliked'}`);
            return result;

        } catch (error: any) {
            console.error('Error toggling like:', error);
            this.setContentError(contentId, error.message);
            throw error;
        } finally {
            this.setContentLoading(contentId, false);
        }
    }

    async recordDownload(contentId: string, fileType?: string, downloadMethod?: string) {
        try {
            console.log(`📥 Recording download for content: ${contentId}`);

            const result = await contentInteractionService.recordDownload(contentId, {
                fileType,
                downloadMethod,
                timestamp: new Date().toISOString()
            });

            runInAction(() => {
                this.updateContentStats(contentId, 'download', 1);
                if (this.rootStore.authStore.currentUser) {
                    this.updateUserInteraction(contentId, 'download', result);
                }
            });

            return result;
        } catch (error: any) {
            console.error('Error recording download:', error);
            this.setContentError(contentId, error.message);
            throw error;
        }
    }

    async recordShare(contentId: string, shareType: 'social_media' | 'email' | 'direct_link' | 'qr_code' | 'embed', platform?: string) {
        try {
            console.log(`📤 Recording share for content: ${contentId}, type: ${shareType}`);

            const result = await contentInteractionService.recordShare(contentId, {
                shareType,
                platform,
                timestamp: new Date().toISOString()
            });

            runInAction(() => {
                this.updateContentStats(contentId, 'share', 1);
                if (this.rootStore.authStore.currentUser) {
                    this.updateUserInteraction(contentId, 'share', result);
                }
            });

            return result;
        } catch (error: any) {
            console.error('Error recording share:', error);
            this.setContentError(contentId, error.message);
            throw error;
        }
    }

    async submitRating(contentId: string, rating: number, comment?: string) {
        if (!this.rootStore.authStore.currentUser) {
            throw new Error('Vui lòng đăng nhập để đánh giá');
        }

        if (rating < 1 || rating > 5) {
            throw new Error('Đánh giá phải từ 1 đến 5 sao');
        }

        this.setContentLoading(contentId, true);

        try {
            console.log(`⭐ Submitting rating for content: ${contentId}, rating: ${rating}`);

            const result = await contentInteractionService.submitRating(contentId, {
                rating,
                comment: comment?.trim()
            });

            runInAction(() => {
                // Update rating stats - more accurate calculation
                const currentStats = this.getStats(contentId);
                const existingUserRating = this.getUserRating(contentId);

                if (existingUserRating) {
                    // Update existing rating
                    const oldTotal = currentStats.averageRating * currentStats.ratingCount;
                    const newTotal = oldTotal - existingUserRating + rating;
                    this.contentStats[contentId] = {
                        ...currentStats,
                        averageRating: newTotal / currentStats.ratingCount
                    };
                } else {
                    // Add new rating
                    const oldTotal = currentStats.averageRating * currentStats.ratingCount;
                    const newTotal = oldTotal + rating;
                    const newCount = currentStats.ratingCount + 1;
                    this.contentStats[contentId] = {
                        ...currentStats,
                        averageRating: newTotal / newCount,
                        ratingCount: newCount
                    };
                }

                this.updateUserInteraction(contentId, 'rating', { rating, comment });
            });

            return result;
        } catch (error: any) {
            console.error('Error submitting rating:', error);
            this.setContentError(contentId, error.message);
            throw error;
        } finally {
            this.setContentLoading(contentId, false);
        }
    }

    // ====================================
    // ✅ ENHANCED: API Methods
    // ====================================

    async getContentStats(contentId: string) {
        try {
            // console.log(`📊 Fetching stats for content: ${contentId}`);
            const stats = await contentInteractionService.getContentStats(contentId);

            runInAction(() => {
                this.contentStats[contentId] = stats;
            });

            return stats;
        } catch (error: any) {
            console.error('Error fetching content stats:', error);
            // Set default stats on error
            runInAction(() => {
                if (!this.contentStats[contentId]) {
                    this.contentStats[contentId] = {
                        viewCount: 0, likeCount: 0, downloadCount: 0, shareCount: 0,
                        averageRating: 0, ratingCount: 0
                    };
                }
            });
            throw error;
        }
    }

    async getUserInteractions(contentId: string) {
        try {
            // console.log(`👤 Fetching user interactions for content: ${contentId}`);
            const interactions = await contentInteractionService.getUserInteractions(contentId);

            runInAction(() => {
                this.userInteractions[contentId] = interactions;
            });

            return interactions;
        } catch (error: any) {
            console.error('Error fetching user interactions:', error);
            runInAction(() => {
                if (!this.userInteractions[contentId]) {
                    this.userInteractions[contentId] = [];
                }
            });
            throw error;
        }
    }

    // ====================================
    // ✅ NEW: Enhanced Helper Methods
    // ====================================

    private updateContentStats(contentId: string, type: string, delta = 1, value?: number) {
        if (!this.contentStats[contentId]) {
            this.contentStats[contentId] = {
                viewCount: 0, likeCount: 0, downloadCount: 0, shareCount: 0,
                averageRating: 0, ratingCount: 0
            };
        }

        const stats = this.contentStats[contentId];

        switch (type) {
            case 'view':
                stats.viewCount = Math.max(0, stats.viewCount + delta);
                break;
            case 'like':
                stats.likeCount = Math.max(0, stats.likeCount + delta);
                break;
            case 'download':
                stats.downloadCount = Math.max(0, stats.downloadCount + delta);
                break;
            case 'share':
                stats.shareCount = Math.max(0, stats.shareCount + delta);
                break;
            case 'rating':
                if (value !== undefined) {
                    const totalRating = stats.averageRating * stats.ratingCount + value;
                    stats.ratingCount = Math.max(0, stats.ratingCount + 1);
                    stats.averageRating = stats.ratingCount > 0 ? totalRating / stats.ratingCount : 0;
                }
                break;
        }
    }

    private updateUserInteraction(contentId: string, type: string, data: any) {
        if (!this.userInteractions[contentId]) {
            this.userInteractions[contentId] = [];
        }

        const interactions = this.userInteractions[contentId];
        const existingIndex = interactions.findIndex(interaction => interaction.type === type);

        const interactionData = {
            id: `${contentId}-${type}-${Date.now()}`,
            type: type as any,
            contentId,
            userId: this.rootStore.authStore.currentUser?.id || '',
            data,
            
        };

        if (existingIndex >= 0) {
            interactions[existingIndex] = interactionData;
        } else {
            interactions.push(interactionData);
        }
    }

    private setContentLoading(contentId: string, loading: boolean) {
        this.loadingStates[contentId] = loading;
        // Backward compatibility
        this.isLoading = Object.values(this.loadingStates).some(Boolean);
    }

    private setContentError(contentId: string, error: string) {
        this.errors[contentId] = error;
        // Backward compatibility - set global error to latest
        this.error = error;
    }

    private clearContentError(contentId: string) {
        this.errors[contentId] = null;
        // Clear global error if no other errors exist
        const hasErrors = Object.values(this.errors).some(error => error !== null);
        if (!hasErrors) {
            this.error = null;
        }
    }

    // ====================================
    // ✅ NEW: Cleanup Methods
    // ====================================

    clearContentData(contentId: string) {
        delete this.contentStats[contentId];
        delete this.userInteractions[contentId];
        delete this.loadingStates[contentId];
        delete this.errors[contentId];
    }

    // Keep existing methods for backward compatibility
    clearError() {
        this.error = null;
        this.errors = {};
    }

    reset() {
        this.interactions = [];
        this.contentStats = {};
        this.userInteractions = {};
        this.loadingStates = {};
        this.errors = {};
        this.isLoading = false;
        this.error = null;
    }
}

export default ContentInteractionStore;